/**
 * TASK MUTATIONS - CREATE, UPDATE, DELETE OPERATIONS
 *
 * This file contains React Query mutation hooks for task modifications:
 * - Task creation with UI store integration
 * - Task updating with optimistic updates
 * - Task deletion with rollback support
 *
 * All mutations follow the optimistic update pattern documented in README.md
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { CLIENT_ID } from '../../../lib/client-context';
import { taskKeys } from '../taskKeys';
import { dashboardKeys } from '../dashboardKeys';
import {
  TaskData,
  FormattedTask,
  CreateTaskInput,
  UpdateTaskInput,
  formatTask,
} from '../taskTypes';
import { db } from '../../../lib/offline-db';
import { queueOfflineOperation } from '../../../lib/offline-sync';
import { getConnectionState } from '../../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../../useQueryErrorHandler';
import {
  invalidateQuery,
  safeUpdateQueryData,
  batchInvalidateQueries,
} from '../../../utils/queryInvalidationUtils';
import { useUser } from '../useAuth';
import { enrichTaskWithAssigneeInfo } from '../../../utils/assigneeHelper';
import { useTaskUIStore } from '../../../store/taskUIStore';
import { normalizeTaskDeadline } from '../../../lib/project-utils';
import { TaskItem } from '../../../types/topic';

/**
 * Enhanced hook for creating a task that integrates with the UI store
 * This combines React Query + Zustand for a cleaner architecture
 * @returns Task creation utilities with UI store integration
 */
export function useTaskCreation() {
  const addTaskMutation = useAddTask();
  const taskUIStore = useTaskUIStore();

  // Create a task using explicit data or falling back to store values
  // This pattern avoids timing issues with store updates
  const createTask = useCallback(
    async (
      status: TaskItem['status'],
      taskData?: {
        title?: string;
        description?: string;
        tags?: string[];
        deadline?: string | null;
        assignedTo?: string | null;
        topicId?: string;
      }
    ) => {
      // IMPORTANT: Get the latest UI state from the store
      const storeState = useTaskUIStore.getState();

      // Use provided values or fall back to store values
      const newTaskTitle = taskData?.title || storeState.newTaskTitle;
      const newTaskDescription = taskData?.description || storeState.newTaskDescription;
      const newTaskTags = taskData?.tags || storeState.newTaskTags;
      const newTaskDeadline = taskData?.deadline || storeState.newTaskDeadline;
      // Fix: Handle assignedTo properly - null is a valid value meaning "unassigned"
      const newTaskAssignedTo =
        taskData?.assignedTo !== undefined ? taskData.assignedTo : storeState.newTaskAssignedTo;
      const topicId = taskData?.topicId || storeState.activeTopicId;

      // Log state for debugging
      logger.component('Task creation - combined data', {
        title: newTaskTitle,
        titleLength: newTaskTitle?.length || 0,
        titleTrimmed: newTaskTitle?.trim(),
        hasTitle: !!newTaskTitle?.trim(),
        status,
        topicId,
        assignedTo: newTaskAssignedTo,
        source: taskData ? 'direct parameters' : 'store state',
      });

      // Validate title
      if (!newTaskTitle?.trim()) {
        throw new Error('Cannot create task with empty title');
      }

      // Validate topic ID
      if (!topicId) {
        throw new Error('No active topic ID');
      }

      // Normalize deadline if needed
      let deadlineStr = null;
      if (newTaskDeadline) {
        deadlineStr = normalizeTaskDeadline(newTaskDeadline);
      }

      // Create task with React Query
      const task = await addTaskMutation.mutateAsync({
        topicId,
        title: newTaskTitle.trim(),
        description: newTaskDescription?.trim() || '',
        status,
        tags: newTaskTags.length > 0 ? newTaskTags : undefined,
        deadline: deadlineStr || undefined,
        assignedTo: newTaskAssignedTo || undefined,
      });

      // Reset form state after successful creation
      taskUIStore.resetTaskForm();

      return task;
    },
    [addTaskMutation, taskUIStore]
  );

  // Format voice input for task creation
  const formatTaskText = useCallback((text: string): string => {
    // Remove trailing period if it exists
    const formattedText = text.trim().replace(/\.$/, '');
    // Capitalize first letter
    return formattedText.charAt(0).toUpperCase() + formattedText.slice(1);
  }, []);

  return {
    createTask,
    formatTaskText,
    isPending: addTaskMutation.isPending,
  };
}

/**
 * Hook for adding a new task with optimistic updates
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddTask() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();
  const { data: user } = useUser();

  return useMutation({
    mutationFn: async (taskInput: CreateTaskInput): Promise<FormattedTask> => {
      const {
        topicId,
        title,
        description = '',
        status,
        tags,
        deadline,
        assignedTo,
        isHighlighted,
      } = taskInput;

      // Add debug logging
      logger.store('Adding new task - debug info', {
        topicId,
        title,
        titleType: typeof title,
        titleLength: title?.length || 0,
        titleTrimmed: title?.trim(),
        trimmedLength: title?.trim()?.length || 0,
        hasTitle: !!title?.trim(),
        status,
        assignedTo,
        is_online: isOnline,
      });

      try {
        // Get existing tasks with the same status to calculate position
        let tasksInSameStatus: TaskData[] = [];

        if (isOnline) {
          // Fetch from server if online
          const { data, error } = await supabase
            .from('tasks')
            .select('position')
            .eq('topic_id', topicId)
            .eq('status', status)
            .order('position');

          if (error) throw error;
          tasksInSameStatus = data as TaskData[];
        } else {
          // Get from IndexedDB if offline
          const allTasks = await db.tasks
            .where('topic_id')
            .equals(topicId)
            .and(task => task.status === status && !task.isDeleted)
            .toArray();

          tasksInSameStatus = allTasks as TaskData[];
        }

        // Sort by position
        tasksInSameStatus.sort((a, b) => (a.position || 0) - (b.position || 0));

        // Find the minimum position (looking for negative values first)
        const minPosition =
          tasksInSameStatus.length > 0
            ? Math.min(...tasksInSameStatus.map(t => t.position || 0))
            : 0;

        // Use a large negative number to ensure new tasks appear at the top
        // If there are already negative positions, go even lower
        const position = minPosition < 0 ? minPosition - 10000 : -10000;

        // Create task data object with metadata
        const now = new Date().toISOString();
        const taskId = crypto.randomUUID();

        // Check if user is authenticated
        if (!user) {
          throw new Error('User not authenticated');
        }

        const taskData: TaskData = {
          id: taskId,
          topic_id: topicId,
          user_id: user.id, // Use the actual user ID from the React Query cache
          title,
          description: description || null,
          status,
          position,
          deadline: deadline || null,
          tags: tags || null,
          is_highlighted: isHighlighted || false,
          assigned_to: assignedTo || null,
          created_at: now,
          updated_at: now,
          metadata: {
            client_id: CLIENT_ID,
            created_at: now,
          },
        };

        // If offline, save locally and queue for sync
        if (!isOnline) {
          // Store in IndexedDB
          await db.tasks.put(taskData);

          // Queue for sync when online
          await queueOfflineOperation('tasks', 'create', taskId, taskData);

          // Enrich with assignee info
          const enrichedTask = enrichTaskWithAssigneeInfo(taskData);

          // Return formatted task
          return formatTask(enrichedTask);
        }

        // Online flow - send to server
        const { data: newTask, error } = await supabase
          .from('tasks')
          .insert([taskData])
          .select()
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.tasks.put(newTask);
        } catch (storageError) {
          logger.error(`Error storing task in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Enrich with assignee info
        const enrichedTask = enrichTaskWithAssigneeInfo(newTask);

        // Return formatted task
        return formatTask(enrichedTask);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'createTask', topicId },
        });
        throw appError;
      }
    },

    // Implement optimistic updates following React Query best practices
    onMutate: async variables => {
      // Cancel any outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: taskKeys.byTopic(variables.topicId) });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<FormattedTask[]>(
        taskKeys.byTopic(variables.topicId)
      );

      // Only proceed with optimistic update if we have a user
      if (user) {
        const now = new Date().toISOString();
        const optimisticId = crypto.randomUUID(); // Create a temporary ID

        // Calculate position for optimistic task (make it appear at the top)
        const existingTasks = previousTasks || [];
        const minPosition =
          existingTasks.length > 0 ? Math.min(...existingTasks.map(t => t.position)) : 0;
        const optimisticPosition = minPosition - 10000;

        // Create optimistic task with data similar to what the server would return
        const optimisticTask: FormattedTask = {
          id: optimisticId,
          topicId: variables.topicId,
          userId: user.id,
          title: variables.title,
          description: variables.description || null,
          status: variables.status,
          position: optimisticPosition,
          deadline: variables.deadline || null,
          tags: variables.tags || null,
          isHighlighted: variables.isHighlighted || false,
          assignedTo: variables.assignedTo || null,
          assigneeName: null, // We can't know this yet
          createdAt: now,
          updatedAt: now,
        };

        // Optimistically update the cache to include new task
        queryClient.setQueryData(
          taskKeys.byTopic(variables.topicId),
          (old: FormattedTask[] | undefined) => {
            return old ? [optimisticTask, ...old] : [optimisticTask];
          }
        );

        // Also set a query cache entry for the individual task
        queryClient.setQueryData(taskKeys.detail(optimisticId), optimisticTask);
      }

      // Return a context with the previous value
      return { previousTasks };
    },

    onSuccess: (newTask, variables) => {
      const { topicId } = variables;

      // Update the task lists with the real task from the server
      // React Query recommends invalidation rather than direct updates in many cases
      queryClient.invalidateQueries({
        queryKey: taskKeys.byTopic(topicId),
        // Use inactive refetch to avoid showing loading spinners
        refetchType: 'active',
      });

      // Add the new task to the detail cache
      queryClient.setQueryData(taskKeys.detail(newTask.id), newTask);

      // Check if we need to update dashboard queries
      if (newTask.assignedTo) {
        // Invalidate assignee-specific queries
        queryClient.invalidateQueries({
          queryKey: taskKeys.byAssignee(newTask.assignedTo),
          refetchType: 'active',
        });

        queryClient.invalidateQueries({
          queryKey: taskKeys.dashboard(newTask.assignedTo),
          refetchType: 'active',
        });
      }

      logger.store('Task created successfully', { taskId: newTask.id });
    },

    onError: (error, variables, context) => {
      // Roll back to the previous state if there was an error
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.byTopic(variables.topicId), context.previousTasks);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to create task. Please try again.',
        context: { operation: 'createTask_optimistic_rollback', topicId: variables.topicId },
      });
    },

    // Always settle all relevant queries after success or error
    onSettled: (data, error, variables) => {
      // This ensures cache is fully in sync with the server
      return queryClient.invalidateQueries({
        queryKey: taskKeys.byTopic(variables.topicId),
      });
    },
  });
}

/**
 * Hook for updating a task
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    // Optimistic update for task updates (including status changes)
    onMutate: async ({
      topicId,
      taskId,
      updates,
    }: {
      topicId: string;
      taskId: string;
      updates: UpdateTaskInput;
    }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: taskKeys.byTopic(topicId) });
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(taskId) });

      // Snapshot previous values for rollback
      const previousTasks = queryClient.getQueryData<FormattedTask[]>(taskKeys.byTopic(topicId));
      const previousTask = queryClient.getQueryData<FormattedTask>(taskKeys.detail(taskId));

      // Optimistically update the task in the topic list
      if (previousTasks) {
        queryClient.setQueryData<FormattedTask[]>(taskKeys.byTopic(topicId), old => {
          if (!old) return old;

          return old.map(task => {
            if (task.id === taskId) {
              const updatedTask = { ...task };

              // Apply all the updates
              if (updates.title !== undefined) updatedTask.title = updates.title;
              if (updates.description !== undefined) updatedTask.description = updates.description;
              if (updates.status !== undefined)
                updatedTask.status = updates.status as TaskItem['status'];
              if (updates.deadline !== undefined) updatedTask.deadline = updates.deadline;
              if (updates.position !== undefined) updatedTask.position = updates.position;
              if (updates.tags !== undefined) updatedTask.tags = updates.tags;
              if (updates.isHighlighted !== undefined)
                updatedTask.isHighlighted = updates.isHighlighted;
              if (updates.assignedTo !== undefined) updatedTask.assignedTo = updates.assignedTo;

              return updatedTask;
            }
            return task;
          });
        });
      }

      // Optimistically update individual task cache
      if (previousTask) {
        queryClient.setQueryData<FormattedTask>(taskKeys.detail(taskId), old => {
          if (!old) return old;

          const updatedTask = { ...old };
          if (updates.title !== undefined) updatedTask.title = updates.title;
          if (updates.description !== undefined) updatedTask.description = updates.description;
          if (updates.status !== undefined)
            updatedTask.status = updates.status as TaskItem['status'];
          if (updates.deadline !== undefined) updatedTask.deadline = updates.deadline;
          if (updates.position !== undefined) updatedTask.position = updates.position;
          if (updates.tags !== undefined) updatedTask.tags = updates.tags;
          if (updates.isHighlighted !== undefined)
            updatedTask.isHighlighted = updates.isHighlighted;
          if (updates.assignedTo !== undefined) updatedTask.assignedTo = updates.assignedTo;

          return updatedTask;
        });
      }

      // Return context for rollback
      return { previousTasks, previousTask };
    },

    mutationFn: async ({
      topicId,
      taskId,
      updates,
    }: {
      topicId: string;
      taskId: string;
      updates: UpdateTaskInput;
    }) => {
      if (!taskId) {
        throw new Error('Task ID is required');
      }

      const now = new Date().toISOString();

      // Filter to keep only valid database fields
      const validDbFields = [
        'title',
        'description',
        'status',
        'deadline',
        'position',
        'tags',
        'is_highlighted',
        'assigned_to',
      ];

      const filteredUpdates: Record<string, unknown> = {
        updated_at: now,
      };

      // Only include defined fields in the update
      Object.entries(updates).forEach(([key, value]) => {
        // Handle both assignedTo (from mutation input) and assigned_to (database field)
        if (key === 'assignedTo' && value !== undefined) {
          filteredUpdates['assigned_to'] = value;
        } else if (key === 'isHighlighted' && value !== undefined) {
          // Handle camelCase to snake_case conversion for highlighted field
          filteredUpdates['is_highlighted'] = value;
        } else if (validDbFields.includes(key) && value !== undefined) {
          filteredUpdates[key] = value;
        }
      });

      // Add metadata
      filteredUpdates.metadata = {
        client_id: CLIENT_ID,
        updated_at: now,
      };

      logger.store('Updating task', {
        taskId,
        topicId,
        updates: updates,
        filteredUpdates: filteredUpdates,
        updatedFields: Object.keys(filteredUpdates),
        is_online: isOnline,
      });

      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current task from IndexedDB
          const currentTask = await db.tasks.get(taskId);

          if (!currentTask || currentTask.isDeleted) {
            throw new Error(`Task ${taskId} not found in offline storage`);
          }

          // Check if status is changing - might need to recalculate position
          const statusChanged = updates.status && updates.status !== currentTask.status;
          if (statusChanged && !updates.position) {
            // Get tasks in the new status to find position
            const tasksInNewStatus = await db.tasks
              .where('topic_id')
              .equals(topicId)
              .and(task => task.status === updates.status && !task.isDeleted)
              .toArray();

            // Sort by position
            tasksInNewStatus.sort((a, b) => (a.position || 0) - (b.position || 0));

            // Find the minimum position
            const minPosition =
              tasksInNewStatus.length > 0
                ? Math.min(...tasksInNewStatus.map(t => t.position || 0))
                : 0;

            // Set position to be before all existing tasks
            filteredUpdates.position = minPosition < 0 ? minPosition - 10000 : -10000;
          }

          // Track original assignee for cache invalidation
          const previousAssignedTo = currentTask.assigned_to;

          // Merge updates with current data
          const updatedTask = {
            ...currentTask,
            ...filteredUpdates,
          };

          // Update in IndexedDB
          await db.tasks.put(updatedTask);

          // Queue for sync when online
          await queueOfflineOperation('tasks', 'update', taskId, updatedTask);

          // Enrich with assignee info
          const enrichedTask = enrichTaskWithAssigneeInfo(updatedTask as TaskData);

          return {
            taskId,
            topicId,
            updates: filteredUpdates,
            previousAssignedTo,
            task: formatTask(enrichedTask),
          };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTask', taskId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      try {
        // Get current task to identify its assignee for cache updates
        const { data: currentTask, error: fetchError } = await supabase
          .from('tasks')
          .select('assigned_to')
          .eq('id', taskId)
          .single();

        if (fetchError) throw fetchError;

        const previousAssignedTo = currentTask?.assigned_to;

        // Update the task
        const { data, error } = await supabase
          .from('tasks')
          .update(filteredUpdates)
          .eq('id', taskId)
          .select()
          .single();

        if (error) throw error;

        // Update in IndexedDB for offline access
        try {
          // Get current data first to preserve fields not included in the update
          const existingTask = await db.tasks.get(taskId);

          if (existingTask) {
            await db.tasks.put({
              ...existingTask,
              ...data,
            });
          }
        } catch (storageError) {
          logger.error(`Error updating task in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Enrich with assignee info
        const enrichedTask = enrichTaskWithAssigneeInfo(data as TaskData);

        return {
          taskId,
          topicId,
          updates: filteredUpdates,
          previousAssignedTo,
          task: formatTask(enrichedTask),
        };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateTask', taskId },
        });
        throw appError;
      }
    },

    // On error, rollback the optimistic update
    onError: (error, variables, context) => {
      // Restore previous task list
      if (context?.previousTasks) {
        queryClient.setQueryData(taskKeys.byTopic(variables.topicId), context.previousTasks);
      }

      // Restore previous individual task
      if (context?.previousTask) {
        queryClient.setQueryData(taskKeys.detail(variables.taskId), context.previousTask);
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to update task. Your changes have been reverted.',
        context: { operation: 'updateTask_optimistic_rollback', taskId: variables.taskId },
      });
    },

    onSuccess: (result, variables) => {
      const { taskId, topicId, task, previousAssignedTo } = result;

      // Build a map of queries to invalidate
      const queryKeysToInvalidate = new Map([
        [taskKeys.detail(taskId), { exact: true }],
        [taskKeys.byTopic(topicId), { exact: true }],
      ]);

      // If assignment changed, invalidate user-specific queries
      if (task.assignedTo !== previousAssignedTo) {
        // Invalidate old assignee's tasks
        if (previousAssignedTo) {
          queryKeysToInvalidate.set(taskKeys.byAssignee(previousAssignedTo), { exact: false });
          queryKeysToInvalidate.set(taskKeys.dashboard(previousAssignedTo), { exact: false });
        }

        // Invalidate new assignee's tasks
        if (task.assignedTo) {
          queryKeysToInvalidate.set(taskKeys.byAssignee(task.assignedTo), { exact: false });
          queryKeysToInvalidate.set(taskKeys.dashboard(task.assignedTo), { exact: false });
        }
      }

      // If highlighted status changed, invalidate dashboard content
      if (variables.updates.isHighlighted !== undefined) {
        queryKeysToInvalidate.set(dashboardKeys.content(), { exact: true });
      }

      // Execute all the invalidations
      batchInvalidateQueries(queryClient, queryKeysToInvalidate, {
        refetchType: 'active',
      });

      // Update task in cache
      safeUpdateQueryData(queryClient, taskKeys.detail(taskId), () => task, {
        context: { operation: 'updateTask', taskId },
      });

      // Update task in topic list
      safeUpdateQueryData<FormattedTask[]>(
        queryClient,
        taskKeys.byTopic(topicId),
        tasks => {
          if (!tasks) return undefined;

          return tasks.map(t => (t.id === taskId ? task : t));
        },
        { context: { operation: 'updateTask', taskId } }
      );
    },
  });
}

/**
 * Hook for deleting a task
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ topicId, taskId }: { topicId: string; taskId: string }) => {
      try {
        logger.store('Deleting task', {
          taskId,
          topicId,
          is_online: isOnline,
        });

        // Get task data first for cache invalidation
        let task: TaskData | null = null;

        if (isOnline) {
          const { data } = await supabase.from('tasks').select('*').eq('id', taskId).single();

          task = data as TaskData;
        } else {
          task = (await db.tasks.get(taskId)) as TaskData;
        }

        if (!task) {
          throw new Error(`Task ${taskId} not found`);
        }

        const assignedTo = task.assigned_to;

        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking task as deleted locally', { taskId });

          try {
            // Mark as deleted in IndexedDB
            await db.tasks.update(taskId, { isDeleted: true });

            // Queue for sync when online
            await queueOfflineOperation('tasks', 'delete', taskId, { id: taskId });

            return { taskId, topicId, assignedTo };
          } catch (offlineError) {
            const error = handleError(offlineError, {
              action: 'toast',
              context: { operation: 'deleteTask', taskId, mode: 'offline' },
            });
            throw error;
          }
        }

        // Online flow - delete from server
        const { error } = await supabase.from('tasks').delete().eq('id', taskId);

        if (error) throw error;

        // Remove from IndexedDB
        try {
          await db.tasks.delete(taskId);
        } catch (storageError) {
          logger.error(`Error deleting task from IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        return { taskId, topicId, assignedTo };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteTask', taskId },
        });
        throw appError;
      }
    },

    // Implement optimistic updates following React Query best practices
    onMutate: async ({ topicId, taskId }) => {
      // Cancel any outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: taskKeys.byTopic(topicId) });
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(taskId) });

      // Snapshot the previous values for potential rollback
      const previousTasks = queryClient.getQueryData<FormattedTask[]>(taskKeys.byTopic(topicId));
      const previousTask = queryClient.getQueryData<FormattedTask>(taskKeys.detail(taskId));

      // Remove task from tasks list optimistically
      queryClient.setQueryData<FormattedTask[]>(taskKeys.byTopic(topicId), old =>
        old ? old.filter(task => task.id !== taskId) : []
      );

      // Remove the individual task from cache
      queryClient.removeQueries({
        queryKey: taskKeys.detail(taskId),
        exact: true,
      });

      // Return context with previous values for rollback
      return { previousTasks, previousTask, taskId, topicId };
    },

    onSuccess: result => {
      const { taskId, assignedTo } = result;

      // If the task was assigned, invalidate the assignee's task lists
      if (assignedTo) {
        invalidateQuery(queryClient, taskKeys.byAssignee(assignedTo), {
          refetchType: 'active',
          context: { operation: 'deleteTask', taskId },
        });

        invalidateQuery(queryClient, taskKeys.dashboard(assignedTo), {
          refetchType: 'active',
          context: { operation: 'deleteTask', taskId },
        });
      }

      logger.store('Task deleted successfully', { taskId });
    },

    // Handle errors by rolling back to previous state
    onError: (error, variables, context) => {
      if (context) {
        // Restore the tasks list if available
        if (context.previousTasks) {
          queryClient.setQueryData(taskKeys.byTopic(context.topicId), context.previousTasks);
        }

        // Restore the individual task if available
        if (context.previousTask) {
          queryClient.setQueryData(taskKeys.detail(context.taskId), context.previousTask);
        }
      }

      // Show user-friendly error message
      handleError(error, {
        action: 'toast',
        customMessage: 'Failed to delete task. The task has been restored.',
        context: { operation: 'deleteTask_optimistic_rollback', taskId: variables.taskId },
      });
    },

    // Always ensure the cache is synchronized after operations
    onSettled: (data, error, variables) => {
      return queryClient.invalidateQueries({
        queryKey: taskKeys.byTopic(variables.topicId),
      });
    },
  });
}
